import { authLogger } from './logger';

/**
 * Utility functions for handling Cognito errors
 */

/**
 * Check if an error is a UserNotConfirmedException from AWS Cognito
 * This handles various formats that Cognito might return the error in
 */
export function isUserNotConfirmedException(error: unknown): boolean {
  if (!error) return false;

  const errorObj = error as any;
  const errorMessage = error instanceof Error ? error.message : String(error);

  // Log the error structure for debugging
  authLogger.debug('Checking error for UserNotConfirmedException:', {
    errorType: typeof error,
    errorName: error instanceof Error ? error.name : 'unknown',
    errorMessage,
    errorObj: {
      __type: errorObj.__type,
      name: errorObj.name,
      code: errorObj.code,
      response: errorObj.response,
      data: errorObj.data
    }
  });

  // Check for the exact Cognito error format: { __type: "UserNotConfirmedException", message: "User is not confirmed." }
  const hasDirectType = errorObj.__type === 'UserNotConfirmedException';
  
  // Check nested response/data objects
  const hasNestedType = (
    (errorObj.response && errorObj.response.__type === 'UserNotConfirmedException') ||
    (errorObj.data && errorObj.data.__type === 'UserNotConfirmedException')
  );
  
  // Check if the error message contains the JSON structure
  const hasJsonStructure = (
    errorMessage.includes('"__type":"UserNotConfirmedException"') ||
    errorMessage.includes('__type":"UserNotConfirmedException')
  );
  
  // Standard error property checks
  const hasStandardProperties = error instanceof Error && (
    error.name === 'UserNotConfirmedException' ||
    errorObj.name === 'UserNotConfirmedException' ||
    errorObj.code === 'UserNotConfirmedException'
  );
  
  // Message content checks
  const hasConfirmationMessage = (
    errorMessage.includes('UserNotConfirmedException') ||
    errorMessage.includes('User is not confirmed') ||
    errorMessage.includes('not confirmed') ||
    errorMessage.toLowerCase().includes('usernotconfirmed') ||
    error.toString().includes('UserNotConfirmedException') ||
    // Exact match for the message you're seeing
    errorMessage === 'User is not confirmed.'
  );

  const isUserNotConfirmed = hasDirectType || hasNestedType || hasJsonStructure || hasStandardProperties || hasConfirmationMessage;

  if (isUserNotConfirmed) {
    authLogger.info('UserNotConfirmedException detected', {
      hasDirectType,
      hasNestedType,
      hasJsonStructure,
      hasStandardProperties,
      hasConfirmationMessage,
      errorMessage
    });
  }

  return isUserNotConfirmed;
}

/**
 * Extract email from error context if available
 */
export function extractEmailFromError(error: unknown): string | null {
  if (!error) return null;

  const errorObj = error as any;
  
  // Try to extract email from various possible locations
  return (
    errorObj.email ||
    errorObj.username ||
    errorObj.user?.email ||
    errorObj.user?.username ||
    errorObj.context?.email ||
    errorObj.context?.username ||
    null
  );
}

/**
 * Create a standardized UserNotConfirmedException error
 */
export function createUserNotConfirmedError(email?: string): Error {
  const message = email 
    ? `UserNotConfirmedException: User ${email} is not confirmed. Please verify your email address.`
    : 'UserNotConfirmedException: User is not confirmed. Please verify your email address.';
    
  const error = new Error(message);
  error.name = 'UserNotConfirmedException';
  
  // Add custom properties for easier detection
  (error as any).__type = 'UserNotConfirmedException';
  (error as any).code = 'UserNotConfirmedException';
  
  return error;
}
